/**
 * 公司信息相关接口
 * 主接口环境: http://47.93.26.53:9082/
 */
import { api, apiConfig } from "@/utils/api";

const DEFAULT_STOCK_CODE = "920873";

/** 接口返回可能是数组或包一层 list/data/result（axios 拦截器已取 response.data） */
type ApiListBody<T> = T[] | { list?: T[]; data?: T[]; result?: T[] };

function getBaseUrl(): string {
  return (apiConfig.primaryUrl || "").replace(/\/$/, "");
}

/** 最新股票价格 */
export interface CompanyPrice {
  stockCode: string;
  stockCompany: string;
  latestPrice: number | string;
  top: number | string;
  low: number | string;
  todayKp: number | string;
  yesterdaySp: number | string;
  endAt: string;
  zdf: number | string;
  zd: number | string;
}

/** 历史股价 */
export interface HistoryPriceItem {
  cjje: string;
  cjl: string;
  jrsp: number | string;
  jrkp: number | string;
  jsrq: string;
  drzd: number | string;
  drzx: number | string;
  zrsp: number | string;
}

/** 当日分时 getCompanyCurrentDatePrice 出参 data 项 */
export interface CurrentDatePriceItem {
  stockCode: string;
  hqzgcj: string;
  hqcjsl: string;
  hqjsrq: string;
  hqgxsj: string;
  hqzdcj: string;
  hqzrsp: string;
  hqjrkp: string;
  hqzjcj: string;
  hqcjje: string;
  [key: string]: unknown;
}

/** 投资者教育/新闻 */
export interface NewsItem {
  title: string;
  url: string;
  publishDate: string;
}

/** 上市公告书 */
export interface ListingAnnouncement {
  id: number;
  filePath: string;
  publishAt: string;
  stockCode: string;
  fileName: string;
}

/** 董事会/高级管理人员 */
export interface CompanyUser {
  id: number;
  userName: string;
  userPermission: string;
  sortFlag: number;
  userType: number;
  stockCode: string;
}

/** 前十大股东筛选项（单条：年报/半年报等） */
export interface TopShareholdersOptionItem {
  id: number;
  year: string;
  typeName: string;
  stockCode: string;
  sortFlag: number;
}

/** 前十大股东筛选项（按年分组，TreeSelect 用） */
export interface TopShareholdersSelectGroup {
  year: string;
  topShareholdersOptions: TopShareholdersOptionItem[];
}

/** 前十大股东筛选项（兼容旧字段） */
export interface SelectOption {
  id: number;
  label?: string;
  name?: string;
  [key: string]: unknown;
}

/** 前十大股东 */
export interface TopShareholder {
  id: number;
  userName: string;
  sortFlag: number;
  cgbl: string;
  type: number;
  changeBl: string;
  cgsl: string;
  changeSl: string;
}

/** 分红 */
export interface StockDividendItem {
  id: number;
  reportPeriod: string;
  cashDividend: string;
  capitalizationShares: string;
  recordDate: string;
  exRightsDate: string;
  paymentDate: string;
  annualCashDividend: string;
  netProfitAttributable: string;
  dividendPayoutRatio: string;
  stockCode: string;
}

/** 分红统计（getStockDividendStat 出参） */
export interface StockDividendStat {
  id?: number;
  stockCode?: string;
  /** 历年累计现金分红总额，如 "874.42万元" */
  totalCashDividend?: string;
  /** 年度平均股利支付率，如 "30.42%" */
  avgDividendPayoutRatio?: string;
  /** 年度平均股息率，如 "-" */
  avgDividendYield?: string;
  /** 当期股息率，如 "-" */
  currentDividendYield?: string;
  [key: string]: unknown;
}

/** 股东变动/股本变动 */
export interface StockShareChangeItem {
  date?: string;
  event?: string;
  increase?: string | number;
  total?: string | number;
  [key: string]: unknown;
}

/** 股东户数 */
export interface ShareHolderCountItem {
  date?: string;
  period?: string;
  count?: number | string;
  [key: string]: unknown;
}

export async function getCompanyPrice(ak: string): Promise<CompanyPrice> {
  const res = (await api.get("/iframeController/getCompanyPrice", {
    params: { ak },
  })) as unknown as { data?: CompanyPrice } & CompanyPrice;
  return res?.data ?? res;
}

export async function getCompanyHistoryPrice(
  ak: string,
  startAt: string,
  endAt: string
): Promise<HistoryPriceItem[]> {
  const res = (await api.get("/iframeController/getCompanyHistoryPrice", {
    params: { ak, startAt, endAt },
  })) as unknown as { data?: HistoryPriceItem[]; list?: HistoryPriceItem[] };
  return Array.isArray(res) ? res : (res?.list ?? res?.data ?? []);
}

/** 当日分时 getCompanyCurrentDatePrice?stockCode= */
export async function getCompanyCurrentDatePrice(
  ak: string
): Promise<CurrentDatePriceItem[]> {
  const res = (await api.get("/iframeController/getCompanyCurrentDatePrice", {
    params: { ak },
  })) as unknown as { data?: CurrentDatePriceItem[] };
  const list = Array.isArray(res) ? res : (res?.data ?? []);
  return Array.isArray(list) ? list : [];
}

export async function getNews(
  ak: string,
  pageNo = 0,
  pageSize = 20
): Promise<NewsItem[]> {
  const res = (await api.get("/iframeController/getNews", {
    params: { ak, pageNo, pageSize },
  })) as unknown as ApiListBody<NewsItem>;
  return Array.isArray(res) ? res : (res?.list ?? res?.data ?? []);
}

export async function getListingAnnouncement(
  ak: string
): Promise<ListingAnnouncement[]> {
  const res = (await api.get("/iframeController/getListingAnnouncement", {
    params: { ak },
  })) as unknown as ApiListBody<ListingAnnouncement>;
  return Array.isArray(res) ? res : (res?.list ?? res?.data ?? []);
}

/** type: 1=董事会, 2=高级管理人员 */
export async function getCompanyUserList(
  ak: string,
  type: 1 | 2
): Promise<CompanyUser[]> {
  const res = (await api.get("/iframeController/getCompanyUserList", {
    params: { ak, type },
  })) as unknown as ApiListBody<CompanyUser>;
  const list = Array.isArray(res)
    ? res
    : (res?.list ?? res?.data ?? res?.result ?? []);
  return Array.isArray(list) ? list : [];
}

/** 前十大股东期数选项（按年分组）type: 1=前十大股东, 2=前十大流通股东 */
export async function getSelectOption(
  ak: string,
  type: 1 | 2 = 1
): Promise<TopShareholdersSelectGroup[]> {
  const res = (await api.get("/iframeController/getSelectOption", {
    params: { ak, type },
  })) as unknown as ApiListBody<TopShareholdersSelectGroup>;
  const list = Array.isArray(res) ? res : (res?.list ?? res?.data ?? []);
  return Array.isArray(list) ? list : [];
}

/** 前十大股东/流通股东列表（期数由 getSelectOption 的 type 区分，此处只传 id） */
export async function getTopShareholders(
  ak: string,
  id: number
): Promise<TopShareholder[]> {
  const res = (await api.get("/iframeController/getTopShareholders", {
    params: { ak, id },
  })) as unknown as ApiListBody<TopShareholder>;
  const list = Array.isArray(res) ? res : (res?.list ?? res?.data ?? []);
  return Array.isArray(list) ? list : [];
}

export async function getStockDividend(
  ak: string
): Promise<{ list: StockDividendItem[]; stat: StockDividendStat }> {
  const res = (await api.get("/iframeController/getStockDividend", {
    params: { ak, type: 1 },
  })) as unknown as ApiListBody<StockDividendItem>;

  // 后端经常存在多种返回结构；这里统一返回 { list, stat } 方便前端直接回显
  if (Array.isArray(res)) {
    return { list: res as StockDividendItem[], stat: {} };
  }

  const root: any = res as any;
  const data: any = root?.data;

  const listCandidate =
    data?.tbStockDividendList ??
    root?.tbStockDividendList ??
    root?.list ??
    data?.list ??
    root?.data ??
    data?.data;

  const list: StockDividendItem[] = Array.isArray(listCandidate)
    ? (listCandidate as StockDividendItem[])
    : [];

  const statCandidate = data ?? root;
  const stat: StockDividendStat = {
    totalCashDividend: statCandidate?.totalCashDividend,
    avgDividendPayoutRatio: statCandidate?.avgDividendPayoutRatio,
    avgDividendYield: statCandidate?.avgDividendYield,
    currentDividendYield: statCandidate?.currentDividendYield,
  };

  return { list, stat };
}

/** 分红统计 getStockDividendStat?stockCode=（支持出参在 res / res.data / res.data[0]） */
export async function getStockDividendStat(
  ak: string
): Promise<StockDividendStat> {
  const res = (await api.get("/iframeController/getStockDividendStat", {
    params: { ak },
  })) as unknown as
    | StockDividendStat
    | { data?: StockDividendStat | StockDividendStat[] };
  if (!res || typeof res !== "object") return {};
  const isStatLike = (v: unknown): v is StockDividendStat => {
    if (!v || typeof v !== "object") return false;
    const obj = v as any;
    return (
      "totalCashDividend" in obj ||
      "avgDividendPayoutRatio" in obj ||
      "avgDividendYield" in obj ||
      "currentDividendYield" in obj
    );
  };

  const data1 = (res as any)?.data;
  if (isStatLike(data1)) return data1 as StockDividendStat;
  if (Array.isArray(data1) && data1.length && isStatLike(data1[0]))
    return data1[0] as StockDividendStat;

  // 二次嵌套：{ data: { data: { ... } } }
  const data2 = data1?.data;
  if (isStatLike(data2)) return data2 as StockDividendStat;
  if (Array.isArray(data2) && data2.length && isStatLike(data2[0]))
    return data2[0] as StockDividendStat;

  // 兼容：有些后端会把统计字段直接放在 res 顶层
  if (isStatLike(res)) return res as StockDividendStat;

  // 最后兜底：递归向下找字段所在对象（多包几层时仍能命中）
  const seen = new Set<unknown>();
  const queue: unknown[] = [res];
  let steps = 0;
  while (queue.length && steps < 50) {
    steps++;
    const cur = queue.shift();
    if (!cur || typeof cur !== "object") continue;
    if (seen.has(cur)) continue;
    seen.add(cur);
    if (isStatLike(cur)) return cur as StockDividendStat;
    for (const value of Object.values(cur as any)) {
      if (value && typeof value === "object") queue.push(value);
    }
  }
  return {};
}

/** 导出分红方案明细 xlsx（GET 返回文件流） */
export function getExportStockDividendUrl(ak: string): string {
  return `${getBaseUrl()}/iframeController/exportStockDividend?ak=${ak}`;
}


export async function getShareHolderCount(
  ak: string
): Promise<ShareHolderCountItem[]> {
  const res = (await api.get("/iframeController/getShareHolderCount", {
    params: { ak },
  })) as unknown as ApiListBody<ShareHolderCountItem>;
  return Array.isArray(res) ? res : (res?.list ?? res?.data ?? []);
}

/** 导出股东数量 */
export function getExportShareHolderCountUrl(ak: string): string {
  return `${getBaseUrl()}/iframeController/exportShareHolderCount?ak=${ak}`;
}

/** 导出前十大股东 type: 1=股东 2=流通股东 */
export function getExportTopShareholdersUrl(
  ak: string,
  id: number,
  type: 1 | 2 = 1
): string {
  return `${getBaseUrl()}/iframeController/exportTopShareholders?ak=${ak}&id=${id}&type=${type}`;
}

/** 上市公告书预览/下载（filePath 为接口返回的路径） */
export function getMeetingFileUrl(
  type: "preview" | "download",
  filePath: string
): string {
  const action = type === "preview" ? "previewFile" : "downloadFile";
  return `${getBaseUrl()}/meetingTemplate/${action}?filePath=${encodeURIComponent(filePath)}`;
}

/** 上传名片：POST 文件，返回 OSS 文件地址 */
export async function uploadBusinessCard(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await api.post<unknown>(
    "/iframeController/uploadBusinessCard",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  const url =
    typeof res === "string" ? res : ((res as { data?: string })?.data ?? "");
  return url;
}

/** 保存留言入参 */
export interface SaveInvestorApplicationParams {
  stockCode?: string;
  investorType: 1 | 2; // 1 机构 2 个人
  name: string;
  businessCard: string; // 名片 OSS 地址
  institution: string;
  phone: string;
  email: string;
  message: string;
}

/** 保存留言 */
export async function saveInvestorApplication(
  params: SaveInvestorApplicationParams,
  stockCode = DEFAULT_STOCK_CODE
): Promise<unknown> {
  const body = {
    stockCode,
    investorType: params.investorType,
    name: params.name,
    businessCard: params.businessCard || "",
    institution: params.institution,
    phone: params.phone,
    email: params.email,
    message: params.message,
  };
  return api.post("/iframeController/saveInvestorApplication", body);
}

/** 督导后台公司图片信息 */
export interface IframeCompanyPicItem {
  id: string;
  companyId: string;
  picType: string;
  picUrl: string;
  picPreviewUrl: string;
}

/** 督导后台公司信息 */
export interface CompanyInfo {
  stockCode?: string;
  stockName?: string;
  userId?: string;
  createAt?: string;
  addCoordinates?: string;
  keyFlag?: string;
  templateType?: string;
  iframeCompanyPicList?: IframeCompanyPicItem[];
  address?: string;
  addressThumbnail?: string;
  phoneNumber?: string;
  fax?: string;
  email?: string;
  lat?: string;
  lng?: string;
}

/**
 * 从督导后台获取公司信息
 * @param ak 唯一标识（如 KGP0X72ST）
 */
export async function getCompanyInfo(ak: string): Promise<CompanyInfo> {
  console.log(ak, "zzzzzz");
  const res = await api.get("/iframeController/getCompanyInfo", {
    params: { ak },
  });
  console.log("[getCompanyInfo] Response:", res);

  // 兼容多种返回结构
  const root: any = res;

  // 尝试从不同层级提取数据
  const candidates = [
    root?.data,
    root,
    root?.result,
    root?.response,
    root?.data?.data,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object") {
      // 检查是否有我们需要的字段
      if (
        "iframeCompanyPicList" in candidate ||
        "stockCode" in candidate ||
        "stockName" in candidate ||
        "address" in candidate ||
        "addressThumbnail" in candidate
      ) {
        console.log("[getCompanyInfo] Using candidate:", candidate);
        return candidate as CompanyInfo;
      }
    }
  }

  return (res as CompanyInfo);
}

/** 投关网站留言列表项 */
export interface InvestorApplicationItem {
  id?: string | number;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
  answerAt?: string;
  status?: 1 | 2; // 1.不展示 2.展示
  answer?: string;
  typeName?: string;
  noticeTitle?: string;
  noticeId?: string | number;
  message?: string;
  [key: string]: unknown;
}

/** 投关网站获取留言列表接口返回 */
interface InvestorApplicationListResponse {
  records?: InvestorApplicationItem[];
  total?: number;
  size?: number;
  current?: number;
  pages?: number;
}

/** 投关网站获取留言列表 */
export async function getEffectiveInvestorApplicationList(
  ak: string,
  pageNo = 1,
  pageSize = 10
): Promise<InvestorApplicationItem[]> {
  const res = (await api.get(
    "/iframeController/getEffectiveInvestorApplicationList",
    {
      params: { ak, pageNo, pageSize },
    }
  )) as unknown as
    | InvestorApplicationItem[]
    | {
        list?: InvestorApplicationItem[];
        data?: InvestorApplicationListResponse | InvestorApplicationItem[];
      };

  let list: InvestorApplicationItem[] = [];

  if (Array.isArray(res)) {
    list = res;
  } else if ((res as any)?.data?.records) {
    list = (res as any).data.records;
  } else if ((res as any)?.data) {
    const data = (res as any).data;
    if (Array.isArray(data)) {
      list = data;
    } else if (data?.records) {
      list = data.records;
    }
  } else if ((res as any)?.list) {
    list = (res as any).list;
  }

  // 只展示 status === 2 的数据
  return Array.isArray(list) ? list.filter((item) => item.status === 2) : [];
}

/** 投关网站保存留言（新接口） */
export async function saveInvestorApplicationNew(
  message: string,
  ak: string
): Promise<unknown> {
  return api.post("/iframeController/saveInvestorApplication", {
    message,
    ak,
  });
}

/** 发送验证码 */
export async function sendVerifyCode(phoneNumber: string): Promise<unknown> {
  return api.get("/sms/sendVerifyCode", {
    params: { phoneNumber },
  });
}

/** 注册+登录 */
export interface IRRegisterOrLoginParams {
  verifyCode: string;
  phoneNumber: string;
}

export async function iRRegisterOrLogin(
  params: IRRegisterOrLoginParams
): Promise<{
  code?: number;
  data?: {
    "dmb.autoLogin.cookie.key"?: string;
    "dmb.logined.userid.session"?: string;
    "dmb.logined.userinfo.session"?: unknown;
    [key: string]: unknown;
  };
  success?: boolean;
  [key: string]: unknown;
}> {
  return api.post("/user/iRRegisterOrLogin", params);
}

/** 保存留言（带 session） */
export async function saveInvestorApplicationWithSession(
  message: string,
  ak: string,
  session: string
): Promise<unknown> {
  return api.post("/iframeController/saveInvestorApplication", {
    message,
    ak,
    session,
  });
}
